import type { Account, Txn, NetWorthData, Rollups, MonthlyRollup, YearlyRollup } from "./types";
import { RETIREMENT_401K_RATIO } from "./gather";
import { contributionAsOf, contributionMatrix } from "./asof";
import { toEmpowerLabel, isResolvableCategory, isIncomeCategory, natureOf } from "./categories";
import { round2 } from "./format";
import { RETIRE_SPLIT_RE, isCalculated401k, accountKey } from "./retirement";
import { buildAccountResolver } from "./accountResolve";

/**
 * Server-side data processing — the TypeScript port of scripts/finance/
 * import_sheet.py, used by the /api/finance/import handler. Cowork submits
 * raw Empower transactions + balance snapshots; this turns them into the
 * canonical Txn/balance records and recomputes the materialized views
 * (net-worth series + monthly/yearly rollups). Keep in sync with the Python
 * importer, which still handles the historical backfill.
 */

/* ── Category / rollup classification ─────────────────────────────────── */

const MONEY_MOVEMENT = [
  "deposit sweep", "loan set-up", "loan setup", "roth conversion",
  "roth in-plan", "wire transfer", "internal transfer",
];

const EMPOWER_CATEGORY_MAP: Record<string, string> = {
  "Gasoline/Fuel": "Gas & Fuel", "Automotive": "Service & Parts",
  "Automotive Expenses": "Service & Parts", "Restaurants/Dining": "Restaurants",
  "Groceries/Supermarkets": "Groceries", "General Merchandise": "Shopping",
  "Clothing/Shoes": "Clothing", "Telephone": "Mobile Phone",
  "Telephone Services": "Mobile Phone", "Cable/Satellite": "Television",
  "Healthcare/Medical": "Doctor", "Entertainment/Recreation": "Entertainment",
  "Home Maintenance": "Home Services", "Mortgages": "Mortgage & Rent",
  "Credit Card Payments": "Credit Card Payment", "Transfers": "Transfer",
  "ATM/Cash": "Cash & ATM", "Checks": "Check", "Deposits": "Income",
  "Paychecks/Salary": "Paycheck", "Interest": "Interest Income",
  "Investment Income": "Dividend & Cap Gains", "Dividends Received": "Dividend & Cap Gains",
  "Retirement Contributions": "Deposit", "Service Charges/Fees": "Fees & Charges",
  "Expense Reimbursement": "Reimbursement", "Refunds/Adjustments": "Returned Purchase",
  "General Rebate/Refund": "Returned Purchase", "Rewards": "CC Cash Back",
  "Charitable Giving": "Charity", "Postage and Shipping": "Shipping",
  "Dues and Subscriptions": "Subscriptions", "Other Income": "Income",
  "Other Expenses": "Misc Expenses",
  // Current getCategories label spellings (Empower renamed several vs the old
  // categoryName vocabulary the map above was built on). See the 2026-07 gather
  // handoff: the UI category comes from categoryId → getCategories, not
  // categoryName, so these current labels are what gets submitted now.
  "Refunds & Reimbursements": "Returned Purchase", "Transit and Parking": "Public Transportation",
  "Professional Services": "Business Services", "Advisory Fee": "Fees & Charges",
  "Loan Payoff": "Transfer", "Hobbies & Classes": "Hobbies", "Postage & Shipping": "Shipping",
  "Dues & Subscriptions": "Subscriptions", "Lawn and Garden": "Lawn & Garden", "Gifts": "Gift",
  "Tax Refund": "Income", "Tax Refunds": "Income", "Loans": "Transfer", "Savings": "Transfer",
};

// Every label the classifier can emit — EMPOWER_CATEGORY_MAP values plus the
// literal returns inside mapEmpowerCategory. Each must resolve to a real group
// in categories.ts, or transactions silently land in "Everything Else".
// Validated at module load so a typo fails the import loudly instead.
const CLASSIFIER_LITERAL_OUTPUTS = [
  "Credit Card Payment", "Sell", "Buy", "Rental Income", "Mortgage & Rent",
  "Cloud Hosting", "Business Services", "Domain Registration", "Subscriptions",
  "Life Insurance", "Auto Insurance", "Home Insurance",
];
{
  const outputs = [...Object.values(EMPOWER_CATEGORY_MAP), ...CLASSIFIER_LITERAL_OUTPUTS];
  const bad = [...new Set(outputs.filter((v) => !isResolvableCategory(v)))];
  if (bad.length) {
    throw new Error(`aggregate.ts: classifier outputs with no group mapping in categories.ts: ${bad.join(", ")}`);
  }
}

export function isMoneyMovement(description: string): boolean {
  const low = description.toLowerCase();
  return MONEY_MOVEMENT.some((p) => low.includes(p));
}

export function mapEmpowerCategory(category: string, description: string, signed: number): string {
  const d = description.toLowerCase();
  if (["autopay", "credit crd", "online payment"].some((p) => d.includes(p))) return "Credit Card Payment";
  if (category === "Securities Trades") return signed > 0 ? "Sell" : "Buy";
  if (category === "Rent") return signed > 0 ? "Rental Income" : "Mortgage & Rent";
  if (category === "Online Services") {
    if (["google cloud", "squarespace", "aws", "amazon web"].some((p) => d.includes(p))) return "Cloud Hosting";
    if (d.includes("appfolio")) return "Business Services";
    if (d.includes("domain")) return "Domain Registration";
    return "Subscriptions";
  }
  // Child/Dependent rows used to be split by description into a hidden
  // "Kids Activities" / "Babysitter & Daycare" subcategory. Retired Oct 2026:
  // the keyword guess misfiled real rows (Cupertino ELOP is after-school care;
  // a City of Cupertino charge is sometimes an adult's dance class), and Kids
  // Activities is now a real category that Cowork assigns per row.
  if (category === "Insurance") {
    if (d.includes("protective")) return "Life Insurance";
    if (["geico", "farmers", "state farm", "progressive"].some((p) => d.includes(p))) return "Auto Insurance";
    return "Home Insurance";
  }
  return EMPOWER_CATEGORY_MAP[category] ?? category;
}

// Rollup is by category NATURE, not amount sign: a return in a spending
// category is negative spending (not income), and a reversal in an income
// category is negative income (not spending). `signed` stays in the signature
// for call-site stability (mapEmpowerCategory already consumed the sign).
/** A transaction's rollup is now purely its category's NATURE — a materialized
 * cache of natureOf(category), never authored independently, so it can't drift
 * from the category (see categories.ts `natureOf`). Money-movement detection
 * moved UP a level: it steers the CATEGORY to Transfers at import time (below),
 * so nature follows from category here rather than overriding it. */
export function deriveRollup(category: string): Txn["rollup"] {
  return natureOf(category);
}

const BUDGET_TYPES = new Set(["Checking", "Savings", "Credit"]);

/** Budgetable classification from the RESOLVED account's type — only cash/debit/
 * credit activity belongs in the transaction ledger. Now that every txn carries
 * an accountId, this replaces the old label-regex accountIsBudgetable (verified
 * identical across all 18,994 stored rows). */
export function budgetableForAccount(account: Account | undefined): boolean {
  return account ? BUDGET_TYPES.has(account.type) : false;
}

/* ── Transaction processing (submission → canonical Txn) ──────────────── */

export interface EmpowerTxn {
  id?: string; // Empower's stable userTransactionId — the dedup key
  date: string; // YYYY-MM-DD or MM/DD/YYYY
  account: string;
  description: string;
  original?: string;
  amount: number; // signed (Empower convention: expenses negative)
  category?: string;
  tags?: string[]; // Empower transaction tags, verbatim
  /**
   * "pending" | "posted" | "unknown" — REQUIRED on import.
   *
   * Copy Empower's own field VERBATIM; never infer it. A pending row is
   * transient: it re-posts later under a NEW userTransactionId, so importing
   * one as though it were posted plants a duplicate that no id-based dedup can
   * ever see. `unknown` is the honest answer when the pull didn't expose the
   * field — it rejects the batch rather than letting a guess through.
   */
  status?: string;
}

/**
 * Validate a raw submission before it's processed. The import is a hard gate:
 * every transaction must carry a `status` of "pending", "posted" or "unknown".
 *
 * `unknown` REJECTS THE WHOLE BATCH by design. It exists so a caller that
 * couldn't read Empower's status field has somewhere honest to put that, rather
 * than guessing "posted" — and guessing is the expensive failure here, because
 * a pending row re-posts under a NEW id and becomes a permanent duplicate.
 * Escalating on "I don't know" is cheap; a silent duplicate is not.
 *
 * `pending` is NOT an error — those rows are dropped and reported (see
 * processTransactions), because the gather is told to submit whole windows.
 *
 * Returns an error string, or null if the batch is clean.
 */
export function validateSubmission(rows: EmpowerTxn[]): string | null {
  let missing = 0;
  let bad = 0;
  let unknown = 0;
  for (const r of rows) {
    const s = r.status == null ? "" : String(r.status).trim().toLowerCase();
    if (!s) missing++;
    else if (s === "unknown") unknown++;
    else if (s !== "pending" && s !== "posted") bad++;
  }
  if (unknown) {
    return `Rejected: ${unknown} transaction(s) have status "unknown". Nothing was imported. `
      + `Re-pull those rows and read Empower's own status field — importing a pending row as posted `
      + `plants a duplicate, because it re-posts later under a new id. If the status genuinely can't `
      + `be determined, leave those rows OUT of the submission and raise it with Jeff.`;
  }
  if (missing || bad) {
    const parts: string[] = [];
    if (missing) parts.push(`${missing} missing a status`);
    if (bad) parts.push(`${bad} with a status other than pending/posted/unknown`);
    return `Rejected: every transaction needs status "pending", "posted" or "unknown" (${parts.join(", ")}). Fix the pull and resubmit.`;
  }
  return null;
}

/** A "reimbursable" tag (in any casing/spelling) means the expense is expected
 * to be paid back, so it's hidden from budgeting by default. The "Excluded"
 * tag is the same mechanism for everything else that shouldn't count toward
 * budgets (Mint's old hard `exclude` flag, migrated Jul 2026 — exclusion is
 * tag-based and policy-controlled; `Txn.exclude` no longer exists — split
 * anchors hide via `split` + `budgetable:false`). MUST stay in sync with
 * tagExcludedByDefault in finance-data-context.tsx — the client's tag deltas
 * assume the precomputed rollups applied exactly this default. */
export function isReimbursable(t: { tags?: string[] }): boolean {
  return !!t.tags?.some((tag) => /reimburs/i.test(tag) || /^excluded$/i.test(tag));
}

function isoDate(d: string): string {
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
  const m = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  return d.slice(0, 10);
}

/** Convert raw Empower rows to canonical Txns, applying category mapping,
 * rules, rollup derivation and budgetable classification. The ledger is
 * FK-only: each row's Empower account label must resolve to a known account —
 * any that don't are returned in `unresolved` (verbatim, deduped) so the import
 * can reject with guidance instead of storing a label-less orphan. */
/** A submitted row that was dropped for being pending — surfaced so the caller
 * can see WHY its submission shrank. */
export interface PendingDrop {
  id?: string;
  date: string;
  description: string;
  amount: number;
}

/**
 * A submission becomes canonical Txn rows. The SUBMITTED CATEGORY IS KEPT: the
 * only transform is mapEmpowerCategory, which translates Empower's label into
 * our taxonomy, plus the money-movement routing below. Nothing here second-
 * guesses the caller's choice.
 *
 * It used to. A set of substring recategorization rules re-applied Jeff's
 * 2026-07 recategorizations on every ingest, because imports rewrote stored
 * rows back then and a rule was the only place a decision could survive. PR #97
 * made stored rows immutable, which removed that need, and the rules were
 * dropped in Aug 2026 — they had become a second authority competing with the
 * row (a $0.20 "Santa Clara County Lib" charge was uncorrectable because the
 * county tax rule claimed it by substring). The drift report already surfaces
 * the ledger's historical category consensus per description, so a correction
 * is applied deliberately at gather time and is visible in the submission.
 */
export function processTransactions(
  rows: EmpowerTxn[],
  accounts: Account[],
): { txns: Txn[]; unresolved: string[]; pending: PendingDrop[] } {
  const out: Txn[] = [];
  const resolveAccount = buildAccountResolver(accounts);
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const unresolved = new Set<string>();
  const pending: PendingDrop[] = [];
  for (const r of rows) {
    // Only posted transactions are stored; pending ones are transient. They are
    // REPORTED, not swallowed: a caller told "40 added" out of 50 submitted,
    // with no account of the other 10, will resubmit them — and when that
    // doesn't work either, relabel them "posted" to force them through. That is
    // the road to a mislabelled pending row, which is the one thing this whole
    // gate exists to prevent.
    if (String(r.status ?? "").trim().toLowerCase() === "pending") {
      pending.push({ id: r.id, date: r.date, description: r.description, amount: Number(r.amount) });
      continue;
    }
    const amount = Number(r.amount);
    if (!Number.isFinite(amount) || amount === 0) continue; // drop $0 noise
    const account = (r.account || "").trim();
    const accountId = resolveAccount(account);
    if (!accountId) {
      unresolved.add(account || "(blank)");
      continue;
    }
    const description = (r.description || "").trim();
    const original = (r.original || r.description || "").trim();
    const signed = round2(amount);
    // Tier 3 (fine detail): the split/renamed label, kept as `subcategory`.
    // Tier 2 (reported): the canonical Empower label it rolls up to.
    const sourceId = r.id != null && String(r.id).trim() !== "" ? String(r.id).trim() : undefined;
    const detail = mapEmpowerCategory((r.category || "Uncategorized").trim(), description, signed);
    let category = toEmpowerLabel(detail);
    // Money-movement descriptions (wires, sweeps, roth conversions) are
    // transfers even when they arrive uncategorized — route them to the
    // Transfers category so the derived rollup follows. (Old behavior overrode
    // the rollup while leaving the category as-is, which is exactly the drift
    // this refactor removes.)
    if (isMoneyMovement(description) && natureOf(category) === "Expense") category = "Transfers";
    const rollup = deriveRollup(category);
    // `tags: []` and no tags field at all are DIFFERENT statements. An absent
    // field is a submission with nothing to say (an override then keeps the
    // stored tags); an explicit empty array is "this row has no tags" (an
    // override then CLEARS them — the only way a tag can be removed through
    // the import). The empty array is normalized away at storage time in
    // mergeTransactions, so stored rows never carry `tags: []`.
    const tags = Array.isArray(r.tags)
      ? r.tags.map((t) => String(t).trim()).filter(Boolean)
      : undefined;
    out.push({
      date: isoDate(r.date),
      description,
      original,
      amount: signed,
      category,
      ...(detail && detail !== category ? { subcategory: detail } : {}),
      accountId,
      ...(sourceId ? { sourceId } : {}),
      ...(tags !== undefined ? { tags } : {}),
      rollup,
      budgetable: budgetableForAccount(byId.get(accountId)),
      source: "empower",
    });
  }
  return { txns: out, unresolved: [...unresolved], pending };
}

// Natural key = date | accountId | amount (NOT description). Descriptions differ
// between the legacy Mint-tab transcription and a fresh Empower pull, so keying
// on them would fail to bridge and create duplicates; date+account+amount is
// stable across sources. This is only the fallback — id-matching is primary.
const naturalKey = (t: { date: string; accountId: string; amount: number }): string =>
  `${t.date}|${t.accountId}|${t.amount}`;

/**
 * The fields an import is allowed to carry. Used to tell a re-sent row that
 * says nothing new (a no-op) from one that would actually change what we hold
 * — the difference between "you re-sent the window, fine" and "you tried to
 * rewrite a stored row". Stored-only fields (note, locked, split) aren't here:
 * an import never supplies them, so their absence isn't a difference.
 */
const IMPORT_FIELDS = ["date", "amount", "description", "original", "accountId", "category"] as const;

const sameStoredRow = (a: Txn, b: Txn): boolean =>
  IMPORT_FIELDS.every((f) => (a[f] ?? null) === (b[f] ?? null))
  && canonTags(a.tags) === canonTags(b.tags);

export interface MergeOptions {
  /** sourceIds the caller deliberately force-overrides. The ONLY way an import
   * can change a row the ledger already holds — the "flag any transactions to
   * override" channel (import `overrideIds`). */
  forceIds?: Set<string>;
}

/**
 * Upsert incoming transactions into the existing set.
 *
 * STORED ROWS ARE IMMUTABLE (Aug 2026). A re-pulled row that matches something
 * the ledger already holds NEVER replaces it — the import can only ADD. The one
 * way through is `forceIds` (the import's `overrideIds`), which makes every
 * change to existing data a deliberate, itemised act.
 *
 * This replaced a 45-day age freeze that left a row's first 45 days
 * unprotected. That window was exactly when curation happens — you rename a
 * merchant the week you get home from a trip — so an ordinary re-pull silently
 * reverted 31 hand-written Barcelona descriptions to their raw issuer strings
 * and nobody noticed for three weeks. Age was never the right question; whether
 * we already hold the row is.
 *
 * Match order:
 *   1. by `sourceId` (Empower's userTransactionId) — the robust key: distinct
 *      same-day/same-amount purchases stay distinct, and a row that shifts
 *      date/amount between pulls still matches.
 *   2. else by natural key (date|account|amount — not description, which drifts
 *      between Mint and Empower), consumed once so two identical incoming rows
 *      can't collapse onto one — this only bridges legacy rows from before ids.
 * Otherwise the row is appended. Nothing is ever dropped or silently lost.
 *
 * A matched row is kept as-is and its key consumed (so the incoming row isn't
 * appended as a duplicate). It is counted as:
 *   - `unchanged` when the incoming row says nothing new — the ordinary case,
 *     since the gather re-pulls an overlap window every run. Not reported by
 *     id: it would be most of the submission and would drown the real signal.
 *   - `protected` when the incoming row DIFFERS from what we hold and wasn't
 *     forced. These ARE reported by id: they're the rows the caller has to look
 *     at and decide about.
 * A forced override replaces the row, keeping its `locked` flag if it had one.
 */
export function mergeTransactions(
  existing: Txn[],
  incoming: Txn[],
  opts: MergeOptions = {},
): { merged: Txn[]; added: number; updated: number; protected: number; unchanged: number; updatedIds: string[]; protectedIds: string[] } {
  const forceIds = opts.forceIds ?? new Set<string>();
  const merged = existing.slice();
  const idIndex = new Map<string, number>();
  // Natural key → FIFO queue of row indices. A queue (not a single index) is
  // essential: when the base holds N rows sharing (date,account,amount) — e.g.
  // two real same-day same-amount purchases the sheet kept as separate rows —
  // N incoming id'd rows must each consume one, or the leftovers orphan as
  // duplicates.
  const natIndex = new Map<string, number[]>();
  const pushNat = (k: string, i: number) => {
    const q = natIndex.get(k);
    if (q) q.push(i);
    else natIndex.set(k, [i]);
  };
  merged.forEach((t, i) => {
    if (t.sourceId) idIndex.set(t.sourceId, i);
    else pushNat(naturalKey(t), i);
  });

  let added = 0;
  let updated = 0;
  let protectedCount = 0;
  let unchanged = 0;
  // Which incoming rows overwrote an EXISTING ledger row — surfaced in the
  // dry-run summary so the gather can drop them and import only new rows
  // (post-import curation lives app-side; a re-pull must not clobber it).
  const updatedIds: string[] = [];
  // Which incoming rows matched a stored row the guard REFUSED to overwrite
  // (locked/curated, a split anchor, or settled before the freeze window).
  // These are deliberately absent from updatedIds — nothing was updated — so
  // an "import only new rows" caller that keys off updatedIds alone reads them
  // as new. The commonest case is a split: the app keeps the un-split parent as
  // a locked ANCHOR under its Empower id, so every re-pull re-sends it and it
  // is protected here. Surfacing the ids lets the gather tell "already handled"
  // apart from "genuinely new".
  const protectedIds: string[] = [];
  for (const t of incoming) {
    let idx = -1;
    if (t.sourceId != null && idIndex.has(t.sourceId)) {
      idx = idIndex.get(t.sourceId)!;
    } else {
      const q = natIndex.get(naturalKey(t));
      if (q && q.length) idx = q.shift()!; // consume one legacy row with this key
    }
    if (idx >= 0) {
      const stored = merged[idx];
      const forced = t.sourceId != null && forceIds.has(t.sourceId);
      if (!forced) {
        // Immutable: keep OUR copy, but consume the key so the incoming row
        // isn't appended as a duplicate. Only a row that would actually have
        // CHANGED something is worth reporting back.
        if (sameStoredRow(stored, t)) unchanged++;
        else {
          protectedCount++;
          if (t.sourceId != null) protectedIds.push(t.sourceId);
        }
        if (t.sourceId != null) idIndex.set(t.sourceId, idx);
        continue;
      }
      // A forced override applies the fields the incoming row actually CARRIES
      // on top of the stored row. It must never replace the row wholesale:
      // `note`, `locked`, `split`/`splitParentId` and any app-only tag (e.g.
      // "Excluded") exist on our side only — an import never sends them — so a
      // wholesale replace deletes them silently, with nothing in the response
      // to say so. `processTransactions` omits `tags` when the submission had
      // no tags FIELD, so the spread preserves stored tags in exactly the case
      // where the import has nothing to say about them — but it carries an
      // explicit `tags: []` through, so an override CAN clear tags. (It
      // couldn't before: [] was collapsed into omission, which made every tag
      // permanent — the mirror image of the pre-#99 wholesale replace.)
      const next: Txn = { ...stored, ...t };
      if (Array.isArray(next.tags) && next.tags.length === 0) delete next.tags; // no-tags is stored as absence
      // category and subcategory are one unit: if the incoming row resolved to
      // a category with no finer detail, the stored subcategory is stale.
      if (!("subcategory" in t)) delete next.subcategory;
      if (stored.locked) next.locked = true;
      merged[idx] = next;
      updated++;
      if (t.sourceId != null) {
        idIndex.set(t.sourceId, idx);
        updatedIds.push(t.sourceId);
      }
    } else {
      if (Array.isArray(t.tags) && t.tags.length === 0) delete t.tags; // never store an empty tag array
      const i = merged.push(t) - 1;
      added++;
      if (t.sourceId != null) idIndex.set(t.sourceId, i);
      else pushNat(naturalKey(t), i);
    }
  }
  // Stable date-desc: return 0 for equal dates so V8's stable sort PRESERVES
  // the relative order of same-day rows (existing rows keep their slot; newly
  // appended rows settle into their date group after the existing ones). The
  // old `? 1 : -1` form returned -1 for equal dates — an inconsistent comparator
  // that rescrambled every same-day group on every import, so materialization
  // saw a fresh row order and rewrote ALL txn shards (defeating incremental
  // materialization + making the stored order non-deterministic).
  merged.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return { merged, added, updated, protected: protectedCount, unchanged, updatedIds, protectedIds };
}

/* ── Split transactions ──────────────────────────────────────────────────── */

export interface SplitPart {
  /** Signed amount, same convention as the parent. Parts must sum to the
   * parent's amount to the cent. */
  amount: number;
  category: string;
  description?: string; // defaults to the parent's
  date?: string; // YYYY-MM-DD; defaults to the parent's
  tags?: string[]; // defaults to the parent's
}

/** Non-anchor rows — what every served view + rollup should see. A split parent
 * is kept in the source-of-truth only to dedup Empower's re-pull, never shown
 * or counted. */
export function visibleTxns(txns: Txn[]): Txn[] {
  return txns.filter((t) => !t.split);
}

/* --------------------------------------------------------------------------
 * Duplicate detection.
 *
 * The import dedups on `sourceId`, which is correct until Empower re-issues
 * ids: a re-pull of an overlapping window then re-sends rows the ledger
 * already holds under DIFFERENT ids, dedup can't recognise them, and both
 * land. That is exactly how eleven duplicated rows reached the Aug 2026
 * ledger, and no id-based guard can ever catch it.
 *
 * So match on what the bank actually reported instead: same day, same
 * account, same raw descriptor, same amount. That is a strong signal — and
 * still NOT proof. Eleven separate $3 "New York City Transit" swipes on one
 * day are eleven real subway rides, indistinguishable by these fields from
 * eleven bad imports. Nothing here may therefore delete anything on its own;
 * it produces CANDIDATES for a human to judge.
 * ---------------------------------------------------------------------- */

export interface DuplicateRow {
  sourceId?: string
  date: string
  description: string
  original?: string
  amount: number
  accountId?: string
  category: string
}

export interface DuplicateGroup {
  /** What the rows agree on — the reason they're grouped. */
  date: string
  accountId: string
  descriptor: string
  amount: number
  /** Every row in the group, oldest sourceId first. Length is always ≥ 2; the
   * count of REDUNDANT rows is `rows.length - 1`. Which one to keep is the
   * caller's call — they're identical by construction. */
  rows: DuplicateRow[]
}

/** The raw descriptor a duplicate is judged on. `original` is what the bank
 * sent; `description` is our curated label, which a rename can change on one
 * copy and not the other — so prefer the untouched field. */
const dupeDescriptor = (t: Txn): string => (t.original || t.description || "").trim().toLowerCase();

/**
 * Rows that look like the same real-world transaction reported twice.
 *
 * Split rows are excluded entirely: an anchor and its children legitimately
 * repeat the parent's date/amount, and re-reporting them as duplicates would
 * invite a delete that breaks the split.
 *
 * @param since optional ISO date — only consider rows on/after it. A full-
 *   ledger scan is fine offline, but the import path only cares about what it
 *   just touched.
 */
export function findDuplicateGroups(txns: Txn[], since?: string): DuplicateGroup[] {
  const groups = new Map<string, Txn[]>();
  for (const t of txns) {
    if (t.split || t.splitParentId) continue;
    if (!t.date || (since && t.date < since)) continue;
    const descriptor = dupeDescriptor(t);
    if (!descriptor) continue;
    const key = [t.date, t.accountId ?? "", descriptor, t.amount].join("\u0000");
    const g = groups.get(key);
    if (g) g.push(t);
    else groups.set(key, [t]);
  }
  const out: DuplicateGroup[] = [];
  for (const rows of groups.values()) {
    if (rows.length < 2) continue;
    const [first] = rows;
    out.push({
      date: first.date,
      accountId: first.accountId ?? "",
      descriptor: dupeDescriptor(first),
      amount: first.amount,
      rows: rows
        .slice()
        .sort((a, b) => (a.sourceId ?? "").localeCompare(b.sourceId ?? ""))
        .map((t) => ({
          sourceId: t.sourceId,
          date: t.date,
          description: t.description,
          original: t.original,
          amount: t.amount,
          accountId: t.accountId,
          category: t.category,
        })),
    });
  }
  return out.sort((a, b) => (a.date === b.date ? a.descriptor.localeCompare(b.descriptor) : b.date.localeCompare(a.date)));
}

/**
 * Duplicate groups THIS SUBMISSION is responsible for — groups containing at
 * least one of `incomingIds`.
 *
 * The distinction matters: a plain full-ledger scan re-reports every historical
 * look-alike on every import (those subway swipes, every month, forever), and a
 * warning that always fires is a warning nobody reads. This fires only when the
 * rows just submitted landed on top of something already there.
 */
export function findImportDuplicates(txns: Txn[], incomingIds: Set<string>): DuplicateGroup[] {
  if (!incomingIds.size) return [];
  return findDuplicateGroups(txns).filter((g) => g.rows.some((r) => r.sourceId && incomingIds.has(r.sourceId)));
}

/* --------------------------------------------------------------------------
 * Description drift — the import-time early-warning that a submission is
 * introducing a NEW description (suggesting close existing ones to reuse) or
 * is re-using a known description with a category/tag set that disagrees with
 * how the ledger has historically filed it. Purely advisory: the summary
 * carries it to Cowork, which decides whether to adjust the input, submit
 * as-is, or ask Jeff.
 * ------------------------------------------------------------------------ */

export interface DriftSuggestion {
  description: string;
  score: number; // 0–1 similarity (or the mapping share when seenBefore)
  /** This exact issuer-string family has been renamed to this label before —
   * the strongest signal (backtested ~80% top-1 on held-out months vs ~60%
   * for pure token similarity). */
  seenBefore?: boolean;
  rows: number; // ledger rows behind this label
  category: string; // the ledger's dominant category for that description
  /** Share of the label's rows in that category. Below 0.8 the label is
   * genuinely multi-category — pick per row, don't copy blindly. */
  categoryShare: number;
  /** Present only when categoryShare < 0.8: the real split. */
  altCategories?: { category: string; share: number }[];
  tags: string[]; // dominant tag set, only when ≥80% of rows agree (else [])
  /** FULL historical category breakdown for this label (top 5, share desc) —
   * how the ledger has actually filed it, so the gather can set the category
   * on the fly instead of copying Empower's. */
  categories: { category: string; share: number }[];
  /** FULL historical tag-set breakdown (top 5, share desc; [] = untagged
   * rows). Same purpose for tags. */
  tagSets: { tags: string[]; share: number }[];
}
export interface DriftReport {
  /** Incoming descriptions the ledger has never seen, with closest matches. */
  newDescriptions: { description: string; rows: number; suggestions: DriftSuggestion[] }[];
  /** Incoming strings whose issuer-string family has historically been mapped
   * to MANY different payees (checks, mobile deposits, Venmo cashouts,
   * card-autopay "thank you" lines). No single suggestion is safe — these need
   * per-row identification (check images, Venmo context, which account paid).
   * `history` is who the family has resolved to before. */
  ambiguous: { description: string; rows: number; history: { description: string; rows: number }[] }[];
  /** Known descriptions whose incoming category/tags disagree with a
   * confident ledger consensus (≥80% of ≥3 rows). */
  mismatches: {
    description: string;
    rows: number;
    field: "category" | "tags";
    incoming: string;
    ledger: string;
    ledgerShare: number;
  }[];
}

const DRIFT_NOISE = /^(the|and|inc|llc|co|corp|corporation|from|for|payment|pymts?|pymnts?|tran|achdd|achdw|ach|ppd|electronic|deposit|withdrawal|credit|debit|xx+\d*|id|web|visa|direct|pos|purchase|recurring)$/;
function driftTokens(s: string): Set<string> {
  return new Set(
    (s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .split(" ")
      .map((w) => w.replace(/\d+$/, "") || w) // payroll709 → payroll (glued counters)
      .filter((w) => w.length >= 2 && !/^\d+$/.test(w) && !DRIFT_NOISE.test(w)),
  );
}
/** Issuer-string family key: lowercase, strip masks (Xxxx1234), numbers,
 * dates, store #s, and rail noise — "SO CAL GAS PAID SCGC XX0124" and
 * "SO CAL GAS PAID SCGC XX0221" collapse to one key. Both sides of the
 * seen-before index use it, so suffix churn never breaks recall. */
function driftKey(s: string): string {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9& ]+/g, " ")
    .replace(/\bx+\d*\b/g, " ")
    .replace(/\b\d[\d/.-]*\b/g, " ")
    .split(" ")
    .map((w) => w.replace(/\d+$/, "") || w)
    .filter((w) => w && !DRIFT_NOISE.test(w))
    .join(" ");
}
/** Case/punct-insensitive label fold, for deduping suggestion variants
 * ("Tj Maxx" / "T J Maxx" / "TJ Maxx" are one suggestion slot). */
const driftFold = (s: string): string =>
  (s || "")
    .toLowerCase()
    .replace(/^the\s+/, "")
    .replace(/[^a-z0-9]+/g, "");
/** Families that are ambiguous BY CONSTRUCTION — the issuer string names the
 * rail, not the payee. Matched against driftKey(). Checks resolve via check
 * images, mobile deposits via the payer, Venmo cashouts may be rent/utility
 * reimbursement carves, "thank you" autopay lines depend on which card. */
const DRIFT_AMBIGUOUS_KEYS = /^(check( check)?( paid| no)?|mobile banking|number|venmo cashout|automatic thank( you)?|thank you( mobile| internet)?|cash( in| out)?|atm|wire|chips)$/;
const canonTags = (tags?: string[]): string => (tags ?? []).map((t) => t.trim()).filter(Boolean).sort().join(", ");

export function describeDrift(
  existing: Txn[],
  incoming: Pick<Txn, "description" | "category" | "tags" | "original">[],
): DriftReport {
  // Ledger history per description: row count, category/tagset histograms, and
  // a token pool drawn from the description AND its originals — the originals
  // encode "all the ways we've mapped" issuer strings onto this label, so an
  // incoming raw string can match the label through them.
  type Hist = { rows: number; cats: Map<string, number>; tags: Map<string, number>; tokens: Set<string> };
  const hist = new Map<string, Hist>();
  // Seen-before index: issuer-string family key → how the ledger has labeled
  // that family. Every curated rename teaches next month's advisory — the
  // index is self-updating, no hand-maintained alias table.
  const seenIndex = new Map<string, Map<string, number>>();
  const indexAdd = (key: string, label: string) => {
    if (!key) return;
    let m = seenIndex.get(key);
    if (!m) seenIndex.set(key, (m = new Map()));
    m.set(label, (m.get(label) ?? 0) + 1);
  };
  for (const t of visibleTxns(existing)) {
    const d = (t.description || "").trim();
    if (!d) continue;
    let h = hist.get(d);
    if (!h) hist.set(d, (h = { rows: 0, cats: new Map(), tags: new Map(), tokens: driftTokens(d) }));
    h.rows++;
    h.cats.set(t.category, (h.cats.get(t.category) ?? 0) + 1);
    const ts = canonTags(t.tags);
    h.tags.set(ts, (h.tags.get(ts) ?? 0) + 1);
    if (t.original && h.tokens.size < 40) for (const tok of driftTokens(t.original)) h.tokens.add(tok);
    if (t.original) indexAdd(driftKey(t.original), d);
    indexAdd(driftKey(d), d);
  }
  const dominant = (m: Map<string, number>): [string, number] => {
    let best = "";
    let n = 0;
    let total = 0;
    for (const [k, v] of m) {
      total += v;
      if (v > n) {
        best = k;
        n = v;
      }
    }
    return [best, total ? n / total : 0];
  };

  // A suggestion's category/tag payload, from the label's ledger history:
  // dominant category + its share; the real split when the label is genuinely
  // multi-category (<80% consensus — Home Depot is GM vs Home Improvement per
  // purchase, Uber is Transit vs Travel per trip: pick per row, don't copy);
  // tags only behind an 80% consensus (one-off Excluded rows are per-row
  // judgment calls, not part of the merchant's standard pattern).
  const histogram = <T,>(m: Map<string, number>, mk: (key: string, share: number) => T): T[] => {
    const total = [...m.values()].reduce((a, b) => a + b, 0);
    return [...m]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([k, n]) => mk(k, Math.round((n / total) * 100) / 100));
  };
  const payloadFor = (label: string, h: Hist, score: number, seenBefore?: boolean): DriftSuggestion => {
    const [cat, catShare] = dominant(h.cats);
    const [ts, tagShare] = dominant(h.tags);
    const out: DriftSuggestion = {
      description: label,
      score: Math.round(score * 100) / 100,
      ...(seenBefore ? { seenBefore } : {}),
      rows: h.rows,
      category: cat,
      categoryShare: Math.round(catShare * 100) / 100,
      tags: tagShare >= 0.8 && ts ? ts.split(", ") : [],
      categories: histogram(h.cats, (category, share) => ({ category, share })),
      tagSets: histogram(h.tags, (set, share) => ({ tags: set ? set.split(", ") : [], share })),
    };
    if (catShare < 0.8) {
      const total = [...h.cats.values()].reduce((a, b) => a + b, 0);
      out.altCategories = [...h.cats]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([c, n]) => ({ category: c, share: Math.round((n / total) * 100) / 100 }));
    }
    return out;
  };

  // Group incoming rows by description, keeping a sample raw original — the
  // rawest form of the issuer string is the best key into the seen-before index.
  const groups = new Map<string, { rows: number; cats: Set<string>; tags: Set<string>; original: string }>();
  for (const t of incoming) {
    const d = (t.description || "").trim();
    if (!d) continue;
    let g = groups.get(d);
    if (!g) groups.set(d, (g = { rows: 0, cats: new Set(), tags: new Set(), original: "" }));
    g.rows++;
    g.cats.add(t.category);
    g.tags.add(canonTags(t.tags));
    if (!g.original && t.original) g.original = t.original.trim();
  }

  const report: DriftReport = { newDescriptions: [], ambiguous: [], mismatches: [] };
  for (const [d, g] of groups) {
    // Tier 0 — the string names a payment rail, not a payee (checks, mobile
    // deposits, Venmo cashouts, autopay thank-yous): no single suggestion is
    // safe, report who the family resolved to before. Checked BEFORE the
    // known-label path: "Deposit Mobile Banking" is a known description (the
    // ledger's own unidentified backlog), and staying silent on it is exactly
    // how that backlog grew.
    const key = driftKey(d);
    const fam = seenIndex.get(key) ?? (g.original ? seenIndex.get(driftKey(g.original)) : undefined);
    const famHistory = () =>
      [...(fam ?? [])]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([label, rows]) => ({ description: label, rows }));
    if (DRIFT_AMBIGUOUS_KEYS.test(key)) {
      report.ambiguous.push({ description: d, rows: g.rows, history: famHistory() });
      continue;
    }
    const h = hist.get(d);
    if (!h) {
      // Never seen. Tier 1 — seen before: this issuer-string family has been mapped
      // already. A ≥60%-dominant family yields its labels directly; a family
      // that scatters across ≥3 distinct payees is ambiguous in the data even
      // without being a known rail string.
      if (fam) {
        const total = [...fam.values()].reduce((a, b) => a + b, 0);
        const entries = [...fam].sort((a, b) => b[1] - a[1]);
        const families = new Set(entries.map(([label]) => driftFold(label)));
        if (entries[0][1] / total >= 0.6) {
          const suggestions = entries
            .filter(([, n]) => n / total >= 0.15)
            .slice(0, 3)
            .map(([label, n]) => payloadFor(label, hist.get(label)!, n / total, true));
          report.newDescriptions.push({ description: d, rows: g.rows, suggestions });
          continue;
        }
        if (families.size >= 3) {
          report.ambiguous.push({ description: d, rows: g.rows, history: famHistory() });
          continue;
        }
      }
      // Tier 2 — lexical: rank every known label. Dice token overlap (token
      // pools include each label's originals) + containment bonus, lifted by
      // three floors backtested on the ledger's own rename history:
      //  · candidate squashed into the incoming string ("thehomedepot" ⊂
      //    "thehomedepot6635") → 0.9
      //  · every incoming token already known for the label → 0.55
      //  · a true truncation pair (SOUTHWES/southwest, ≥5-char shared prefix,
      //    unequal tokens) → 0.6
      // Near-ties resolve to the label with more rows (the ledger's dominant
      // spelling), and one suggestion slot per folded family.
      const inTok = driftTokens(d);
      const inNorm = [...inTok].sort().join(" ");
      // Squash of the KEPT tokens (in string order), not the raw string — raw
      // squashing invents cross-boundary matches ("cupertino CA PURCHASE" ⊃
      // "chase"); noise words and digit runs never get to fuse into a brand.
      const inSquash = [...inTok].join("");
      const scored: { score: number; rows: number; label: string; h: Hist }[] = [];
      for (const [cand, ch] of hist) {
        let overlap = 0;
        for (const tok of inTok) if (ch.tokens.has(tok)) overlap++;
        let score = inTok.size + ch.tokens.size ? (2 * overlap) / (inTok.size + ch.tokens.size) : 0;
        const candNorm = [...driftTokens(cand)].sort().join(" ");
        if (inNorm && candNorm && (inNorm.includes(candNorm) || candNorm.includes(inNorm))) score = Math.min(1, score + 0.3);
        if (inTok.size && overlap === inTok.size) score = Math.max(score, 0.55);
        const candSquash = [...driftTokens(cand)].join("");
        if (candSquash.length >= 5 && inSquash.includes(candSquash)) score = Math.max(score, 0.9);
        else if (score < 0.6) {
          truncation: for (const a of inTok)
            for (const b of ch.tokens) {
              if (a === b) continue;
              const m = Math.min(a.length, b.length);
              if (m >= 5 && a.slice(0, m) === b.slice(0, m)) {
                score = 0.6;
                break truncation;
              }
            }
        }
        if (score >= 0.34) scored.push({ score, rows: ch.rows, label: cand, h: ch });
      }
      // Bucketed sort: scores within a ~0.12 band count as tied and resolve to
      // the label with more rows (the ledger's dominant spelling). Bucketing —
      // not pairwise closeness — keeps the comparator a consistent total order.
      const band = (s: number) => Math.round(s / 0.12);
      scored.sort((a, b) => band(b.score) - band(a.score) || b.rows - a.rows || b.score - a.score || a.label.localeCompare(b.label));
      const seenFold = new Set<string>();
      const suggestions: DriftSuggestion[] = [];
      for (const s of scored) {
        const f = driftFold(s.label);
        if (seenFold.has(f)) continue;
        seenFold.add(f);
        suggestions.push(payloadFor(s.label, s.h, s.score));
        if (suggestions.length === 3) break;
      }
      report.newDescriptions.push({ description: d, rows: g.rows, suggestions });
      continue;
    }
    // Known description: flag category/tag disagreement against a confident
    // consensus only (≥80% of ≥3 rows) so genuinely mixed labels stay quiet.
    const [cat, catShare] = dominant(h.cats);
    if (h.rows >= 3 && catShare >= 0.8) {
      for (const c of g.cats)
        if (c !== cat)
          report.mismatches.push({ description: d, rows: g.rows, field: "category", incoming: c, ledger: cat, ledgerShare: Math.round(catShare * 100) / 100 });
    }
    const [ts, tagShare] = dominant(h.tags);
    if (h.rows >= 3 && tagShare >= 0.8) {
      for (const t of g.tags)
        if (t !== ts)
          report.mismatches.push({ description: d, rows: g.rows, field: "tags", incoming: t || "(none)", ledger: ts || "(none)", ledgerShare: Math.round(tagShare * 100) / 100 });
    }
  }
  // Deterministic order, biggest first; the import summary caps the payload.
  report.newDescriptions.sort((a, b) => b.rows - a.rows);
  report.ambiguous.sort((a, b) => b.rows - a.rows);
  report.mismatches.sort((a, b) => b.rows - a.rows);
  return report;
}

const splitChildId = (sourceId: string, i: number): string => `${sourceId}:split:${i}`;

/**
 * Split the transaction with `sourceId` into N parts (each its own date,
 * description, amount, category, tags). Empower's export carries NO split
 * structure — it re-sends the whole un-split parent on every pull — so a split
 * is ours to keep: the parent becomes a hidden, locked, excluded ANCHOR
 * (retained only to dedup that re-pull via the overwrite guard), and N child
 * rows carry the real money. Parts must sum to the parent amount to the cent.
 * Throws on any validation failure. Pure — returns a new array.
 */
export function splitTransaction(
  txns: Txn[],
  sourceId: string,
  parts: SplitPart[],
  accounts: Account[],
): { txns: Txn[]; parentId: string; childIds: string[] } {
  if (!Array.isArray(parts) || parts.length < 2) throw new Error("a split needs at least 2 parts");
  const idx = txns.findIndex((t) => t.sourceId === sourceId && !t.split);
  if (idx < 0) throw new Error(`no splittable transaction with sourceId ${sourceId}`);
  const parent = txns[idx];
  for (const p of parts) {
    if (!Number.isFinite(p.amount) || round2(p.amount) === 0) throw new Error("each split part needs a nonzero amount");
    if (!p.category || !String(p.category).trim()) throw new Error("each split part needs a category");
  }
  const sum = round2(parts.reduce((a, p) => a + p.amount, 0));
  if (sum !== round2(parent.amount)) {
    throw new Error(`split parts sum to ${sum}, must equal the parent amount ${round2(parent.amount)}`);
  }

  const budgetable = budgetableForAccount(accounts.find((a) => a.id === parent.accountId));
  const children: Txn[] = parts.map((p, i) => {
    const description = (p.description ?? parent.description).trim();
    const amount = round2(p.amount);
    const category = String(p.category).trim();
    const tags = (p.tags ?? parent.tags)?.map((t) => String(t).trim()).filter(Boolean);
    return {
      date: p.date ? isoDate(p.date) : parent.date,
      description,
      original: parent.original,
      amount,
      category,
      accountId: parent.accountId,
      sourceId: splitChildId(sourceId, i),
      splitParentId: sourceId,
      ...(tags && tags.length ? { tags } : {}),
      rollup: deriveRollup(category),
      budgetable,
      locked: true,
      source: parent.source,
    };
  });

  const out = txns.slice();
  // Anchor: hidden (split) + non-budgetable + locked so an Empower re-pull of
  // the un-split parent is protected (kept as the anchor) instead of reviving
  // a real row.
  out[idx] = { ...parent, split: true, budgetable: false, locked: true };
  out.push(...children);
  // Stable date-desc (see mergeTransactions) — 0 for equal dates preserves order.
  out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return { txns: out, parentId: sourceId, childIds: children.map((c) => c.sourceId!) };
}

/** Reverse a split: drop the children and restore the parent as a normal row. */
export function unsplitTransaction(
  txns: Txn[],
  sourceId: string,
  accounts: Account[],
): { txns: Txn[]; removed: number } {
  const parent = txns.find((t) => t.sourceId === sourceId && t.split);
  if (!parent) throw new Error(`no split parent with sourceId ${sourceId}`);
  const kept = txns.filter((t) => t.splitParentId !== sourceId);
  const restored = kept.map((t) => {
    if (t !== parent) return t;
    const { split: _split, splitParentId: _sp, locked: _locked, ...rest } = t;
    return { ...rest, budgetable: budgetableForAccount(accounts.find((a) => a.id === t.accountId)) };
  });
  return { txns: restored, removed: txns.length - kept.length };
}

/* ── Balance snapshots → account balances + carry-forward ─────────────── */

export interface BalanceRow {
  date: string;
  institution: string;
  owner: string;
  type?: string;
  account: string; // matches Account.name (or "<prefix> - INPUT" for 401k)
  balance: number;
  notes?: string;
}

/** Apply a batch of dated balance snapshots. Each row lands as a point at its
 * own ISO date (YYYY-MM-DD) — the daily model, so multiple same-account
 * snapshots across days each survive (a same-day re-submit overwrites that
 * day's point). There is NO carry-forward write: an account not in this batch
 * keeps its prior points and is summed as-of at read time (see contributionAsOf
 * / computeAggregatesFor), so nothing gets copied into a slot it wasn't
 * observed in. Splits a "<prefix> - INPUT" 401k total into its Roth/Traditional
 * accounts by the fixed per-plan ratio. Returns the affected snapshot date(s). */
export function applyBalances(accounts: Account[], rows: BalanceRow[]): string[] {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const dates = new Set<string>();
  const warnings: string[] = [];
  // Balance dates are PACIFIC-TIME calendar days. An evening gather runs after
  // 5pm PT, when the UTC date has already rolled over — a submitter stamping
  // "today" in UTC dates the snapshot tomorrow (seen 2026-07-16: a 7:05pm PT
  // run produced a phantom next-day balance point). Clamp any future-PT date
  // to today-PT rather than storing an observation from a day that hasn't
  // happened yet.
  const todayPT = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date());
  let clamped = 0;

  // Build INPUT → {roth, traditional} pairs from account names.
  const calcPairs = new Map<string, { roth?: Account; traditional?: Account }>();
  for (const a of accounts) {
    const m = a.name.match(RETIRE_SPLIT_RE);
    if (m && isCalculated401k(a)) {
      const key = accountKey(a.institution, a.owner, m[1]);
      const pair = calcPairs.get(key) ?? {};
      pair[m[2].toLowerCase() as "roth" | "traditional"] = a;
      calcPairs.set(key, pair);
    }
  }
  // Roth share of a 401k total is a FIXED ratio per plan (from the sheet's
  // historical Vanguard/Fidelity per-account data), keyed by the account-name
  // prefix ("Google 401k", "Anthropic 401k"). Cowork submits only the Empower
  // total; the app derives Roth/Traditional deterministically from this ratio.
  const rothFactor = (prefix: string): number | null => RETIREMENT_401K_RATIO[prefix] ?? null;

  for (const r of rows) {
    let day = isoDate(r.date);
    if (day > todayPT) {
      day = todayPT;
      clamped++;
    }
    const bal = round2(Number(r.balance));
    if (!Number.isFinite(bal)) continue;
    dates.add(day);
    const inputMatch = r.account.match(/^(.*?) - INPUT$/);
    if (inputMatch) {
      const pair = calcPairs.get(accountKey(r.institution, r.owner, inputMatch[1]));
      const f = rothFactor(inputMatch[1]);
      if (pair?.roth && pair.traditional && f != null) {
        pair.roth.balances[day] = round2(bal * f);
        pair.traditional.balances[day] = round2(bal * (1 - f));
        pair.roth.asOf = day;
        pair.traditional.asOf = day;
        if (pair.roth.balances[day] !== 0) pair.roth.active = true;
        if (pair.traditional.balances[day] !== 0) pair.traditional.active = true;
      } else {
        warnings.push(`${r.institution} | ${r.owner} | ${r.account} (no Roth factor)`);
      }
      continue;
    }
    let acct = byId.get(accountKey(r.institution, r.owner, r.account));
    if (!acct) {
      const matches = accounts.filter(
        (a) => a.institution === r.institution && a.owner === r.owner && a.name === r.account,
      );
      acct = matches.length === 1 ? matches[0] : undefined;
    }
    if (!acct) {
      warnings.push(`${r.institution} | ${r.owner} | ${r.account}`);
      continue;
    }
    acct.balances[day] = bal;
    acct.asOf = day;
    // A nonzero balance means the account is live; a $0 submission leaves the
    // flag alone (a paid-off card stays active, an emptied one stays inactive).
    if (bal !== 0) acct.active = true;
  }

  if (clamped) {
    console.warn(`applyBalances: ${clamped} future-dated balance rows clamped to ${todayPT} (PT)`);
  }
  if (warnings.length) {
    console.warn("applyBalances: unresolved accounts:", warnings);
  }
  return [...dates];
}

/* ── Aggregate net-worth series ───────────────────────────────────────── */

const FIRE_EXCLUDED_TYPES = new Set(["Education", "DCFSA", "HSA", "Crypto", "Custodial"]);
/** Investment properties: the Real Estate account AND the Mortgage account
 * share the property's name. Used by the FIRE exclusion below (the property
 * is out of FIRE scope) and by the Savings view's "Rental property" line
 * (its tag-excluded cash flow + its mortgage paydown, reported as one unit). */
export const RENTAL_PROPERTY_NAMES = new Set(["2259 Bonnie Brae Ave"]);
export function isRentalPropertyAccount(a: Pick<Account, "type" | "name">): boolean {
  return (a.type === "Real Estate" || a.type === "Mortgage") && RENTAL_PROPERTY_NAMES.has(a.name);
}
const FIRE_EXCLUDED_ACCOUNTS = new Set([...RENTAL_PROPERTY_NAMES].map((n) => `Real Estate|${n}`));
/** Whether an account's value counts toward FIRE net worth (the Savings view
 * decomposes ΔFIRE NW, so it must draw the boundary exactly here). */
export function isFireScopeAccount(a: Pick<Account, "type" | "name">): boolean {
  return !FIRE_EXCLUDED_TYPES.has(a.type) && !FIRE_EXCLUDED_ACCOUNTS.has(`${a.type}|${a.name}`);
}
const CASH_TYPES = new Set(["Checking", "Savings", "Stock", "Crypto", "Credit"]);

/** Fold one date's per-account contributions into the 9 aggregate metrics.
 * Each component metric is rounded before the derived ones combine, matching
 * the historical (per-metric) rounding exactly. */
function foldAggregates(accounts: Account[], contribution: (a: Account, i: number) => number): Record<string, number> {
  let assets = 0, debts = 0, fireExcluded = 0, allLoans = 0, creditCards = 0, cash = 0, nonInvestment = 0, retirement = 0;
  for (let i = 0; i < accounts.length; i++) {
    const a = accounts[i];
    const v = contribution(a, i);
    if (a.isLiability) debts += v;
    else assets += v;
    if (!isFireScopeAccount(a)) fireExcluded += v;
    if (a.type === "Loan" || a.type === "Mortgage") allLoans += v;
    if (a.type === "Credit") creditCards += v;
    if (CASH_TYPES.has(a.type)) cash += v;
    if (a.type === "Checking" || a.type === "Savings" || a.type === "Real Estate") nonInvestment += v;
    if (a.type === "Retirement") retirement += v;
  }
  assets = round2(assets);
  debts = round2(debts);
  const netWorth = round2(assets + debts);
  return {
    assets,
    debts,
    netWorth,
    fireNetWorth: round2(netWorth - round2(fireExcluded)),
    allLoans: round2(allLoans),
    creditCards: round2(creditCards),
    cash: round2(cash),
    investments: round2(assets - round2(nonInvestment)),
    retirement: round2(retirement),
  };
}

// Each account contributes its AS-OF value on `date` (most-recent observation
// on or before it; closed accounts drop out after their last observation).
export function computeAggregatesFor(accounts: Account[], date: string): Record<string, number> {
  return foldAggregates(accounts, (a) => contributionAsOf(a, date));
}

/** Rebuild the full net-worth series — one point per distinct observation date
 * across all accounts, each an as-of sum. (Fallback path; production splices
 * touched dates into the seeded series via mergeNetWorth to preserve history.)
 * One forward sweep carries each account's last-seen value across the date
 * axis — NOT a per-date balanceAsOf scan, which is O(dates × accounts ×
 * balance-keys) and blows up on 16 years of daily balances. */
export function computeNetWorth(accounts: Account[]): NetWorthData {
  const dateSet = new Set<string>();
  for (const a of accounts) for (const d of Object.keys(a.balances)) dateSet.add(d);
  const months = [...dateSet].sort();

  const contributions = contributionMatrix(accounts, months);
  const series: Record<string, Record<string, number>> = {};
  months.forEach((d, mi) => {
    const row = contributions[mi];
    const agg = foldAggregates(accounts, (_a, i) => row[i]);
    for (const [k, v] of Object.entries(agg)) (series[k] ??= {})[d] = v;
  });
  return { months, series };
}

/**
 * Recompute only `months` and splice them into an existing net-worth series,
 * leaving all other months untouched. Historical aggregates in the seeded
 * series derive from the source sheet's own formulas (e.g. fireNetWorth's
 * fixed row-range sum), which diverge from the current type-based FIRE
 * definition for old account compositions; preserving them keeps displayed
 * history stable while new/updated months use the forward-consistent
 * definition. Falls back to a full recompute when there is no prior series.
 */
export function mergeNetWorth(
  existing: NetWorthData | null | undefined,
  accounts: Account[],
  months: string[],
): NetWorthData {
  if (!existing || !existing.months?.length) return computeNetWorth(accounts);
  const series: Record<string, Record<string, number>> = {};
  for (const [k, m] of Object.entries(existing.series)) series[k] = { ...m };
  for (const month of months) {
    const agg = computeAggregatesFor(accounts, month);
    for (const [k, v] of Object.entries(agg)) (series[k] ??= {})[month] = v;
  }
  const monthSet = new Set<string>();
  for (const m of Object.values(series)) for (const key of Object.keys(m)) monthSet.add(key);
  return { months: [...monthSet].sort(), series };
}

/* ── Monthly / yearly rollups (statements) ────────────────────────────── */

export function computeRollups(txns: Txn[]): Rollups {
  const monthly = new Map<string, { income: number; spending: number; byCategory: Record<string, number> }>();
  const yearly = new Map<string, { income: number; spending: number; byCategory: Record<string, number> }>();
  const bucket = (map: typeof monthly, key: string) => {
    let b = map.get(key);
    if (!b) map.set(key, (b = { income: 0, spending: 0, byCategory: {} }));
    return b;
  };
  for (const t of txns) {
    if (t.split) continue; // split anchor — its children carry the money
    if (!t.budgetable) continue;
    // Reimbursable txns are hidden from budgeting by default, so they're left
    // out of the precomputed statements; the app adds them back client-side
    // when the "include reimbursable" toggle is on (see useMonthlyAgg).
    if (isReimbursable(t)) continue;
    if (t.rollup !== "Expense" && t.rollup !== "Income") continue;
    for (const b of [bucket(monthly, t.date.slice(0, 7)), bucket(yearly, t.date.slice(0, 4))]) {
      if (t.rollup === "Income") b.income += t.amount;
      else {
        const spent = -t.amount;
        b.spending += spent;
        b.byCategory[t.category] = (b.byCategory[t.category] ?? 0) + spent;
      }
    }
  }
  const finalize = (map: typeof monthly) =>
    [...map.keys()].sort().map((key) => {
      const b = map.get(key)!;
      const byCategory = Object.fromEntries(
        Object.entries(b.byCategory)
          .filter(([, v]) => Math.abs(v) >= 0.005)
          .sort((a, c) => c[1] - a[1])
          .map(([c, v]) => [c, round2(v)]),
      );
      return {
        key,
        income: round2(b.income),
        spending: round2(b.spending),
        net: round2(b.income - b.spending),
        byCategory,
      };
    });
  return {
    monthly: finalize(monthly).map(({ key, ...r }): MonthlyRollup => ({ month: key, ...r })),
    yearly: finalize(yearly).map(({ key, ...r }): YearlyRollup => ({ year: key, ...r })),
  };
}

